import { useState } from "react";
import { Download, FileSpreadsheet } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function ImportDialog({ open, onOpenChange, onImported }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    const r = await api.get("/recipes-import/template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = "modello_ricette.xlsx"; a.click(); URL.revokeObjectURL(url);
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try { const r = await api.post("/recipes-import/preview", fd); setPreview(r.data); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post("/recipes-import/confirm", { recipes: preview.recipes.filter((x) => x.errors === 0 && !x.exists) });
      toast.success(`${r.data.created.length} ricette importate come bozza`); setPreview(null); onOpenChange(false); onImported();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const importable = preview?.recipes.filter((x) => x.errors === 0 && !x.exists).length || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) setPreview(null); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Importa ricette da Excel o CSV</DialogTitle>
          <DialogDescription>Colonne richieste: ricetta, categoria, porzioni, prezzo, articolo, quantita, unita. Una riga per ingrediente. Gli articoli devono già esistere in anagrafica.</DialogDescription></DialogHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button"><Download className="h-4 w-4" /> Scarica modello</Button>
          <label className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90">
            <FileSpreadsheet className="h-4 w-4" /> Scegli file<input type="file" accept=".csv,.xlsx" className="hidden" onChange={onFile} data-testid="import-file-input" />
          </label>
          {busy && <span className="text-sm text-slate-500">Elaborazione…</span>}
        </div>
        {preview && (
          <div className="space-y-3" data-testid="import-preview">
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="badge badge-green">{importable} ricette pronte</span>
              {preview.error_count > 0 && <span className="badge badge-red">{preview.error_count} righe con errori</span>}
              {preview.recipes.some((x) => x.exists) && <span className="badge badge-amber">{preview.recipes.filter((x) => x.exists).length} già esistenti (saltate)</span>}
            </div>
            <div className="max-h-[320px] overflow-auto rounded-md border">
              <table className="dense-table w-full text-sm">
                <thead className="sticky top-0 bg-slate-50"><tr><th>Riga</th><th>Ricetta</th><th>Articolo</th><th className="text-right">Qtà</th><th>Esito</th></tr></thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.line} className={r.errors.length ? "bg-[#FEE2E2]/40" : ""} data-testid={`import-row-${r.line}`}>
                      <td className="tnum">{r.line}</td><td>{r.ricetta}</td><td>{r.articolo}</td><td className="tnum text-right">{r.quantita} {r.unita}</td>
                      <td>{r.errors.length ? <span className="text-[#7F1D1D]">{r.errors.join("; ")}</span> : <span className="text-[#15803D]">OK</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setPreview(null)}>Annulla</Button>
              <Button onClick={confirm} disabled={busy || importable === 0} data-testid="import-confirm-button">Conferma importazione ({importable})</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
